import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Check, Building2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { workspacesApi } from "../../api/endpoints";

/**
 * WorkspaceSwitcher — dropdown of the user's memberships. Never lists a
 * workspace the user isn't a member of; the API only returns that set.
 */
export function WorkspaceSwitcher() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    workspacesApi.list()
      .then(setWorkspaces)
      .catch(() => setWorkspaces([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = workspaces.find((w) => w.id === workspaceId);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="workspace-switcher"
        className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-slate-300"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Building2 size={14} className="text-slate-500" aria-hidden="true" />
        <span className="max-w-[16rem] truncate text-slate-800">
          {loading ? "Loading…" : current?.name || "Choose workspace"}
        </span>
        <ChevronDown size={14} className="text-slate-400" aria-hidden="true" />
      </button>
      {open && (
        <ul
          role="listbox"
          data-testid="workspace-switcher-menu"
          className="absolute left-0 z-50 mt-1 w-72 overflow-hidden rounded border border-slate-200 bg-white shadow-lg"
        >
          {workspaces.length === 0 && (
            <li className="px-3 py-3 text-sm text-slate-500">
              No workspaces yet.
            </li>
          )}
          {workspaces.map((w) => {
            const active = w.id === workspaceId;
            return (
              <li key={w.id}>
                <button
                  type="button"
                  data-testid={`workspace-option-${w.id}`}
                  onClick={() => {
                    setOpen(false);
                    navigate(`/w/${w.id}/dashboard`);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50",
                    active && "bg-slate-50",
                  )}
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-900">
                      {w.name}
                    </div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wide">
                      {w.plan}
                    </div>
                  </div>
                  {active && <Check size={14} className="text-emerald-600" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
